import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ClipboardList, Plus, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const STAGES = [
  'Land Acquisition','Approvals','Plan Sanction','Excavation',
  'Plinth','Structure','Brickwork','Plaster',
  'Plumbing','Electrical','Flooring','Painting',
  'Finishing','Possession','Handover',
];

const PROJECTS_SEED = [
  { srNo: 1, project: 'Sai Plaza (Rajgurunagar)', stage: 'Finishing', completion: 92, contact: 'Sahil', remark: 'Final inspection' },
  { srNo: 2, project: 'Office Dream Riviera', stage: 'Possession', completion: 100, contact: 'Sahil', remark: 'Handed over' },
  { srNo: 3, project: 'Bramha Heights', stage: 'Painting', completion: 78, contact: 'Sahil', remark: 'Block A done' },
  { srNo: 4, project: 'Godavari Heights', stage: 'Brickwork', completion: 45, contact: 'Sahil', remark: 'On schedule' },
  { srNo: 5, project: 'Kaveri Heights', stage: 'Structure', completion: 30, contact: 'Sahil', remark: 'Phase 1' },
  { srNo: 6, project: 'Krushna Heights', stage: 'Plinth', completion: 18, contact: 'Sahil', remark: 'Foundation done' },
  { srNo: 7, project: 'Dreams Kokan City', stage: 'Approvals', completion: 8, contact: 'Sahil', remark: 'RERA pending' },
  { srNo: 8, project: 'Dreams Nagpur City', stage: 'Land Acquisition', completion: 4, contact: 'Sahil', remark: 'Survey in progress' },
];

const stageColor = (pct) => {
  if (pct >= 90) return 'bg-green-600 text-white';
  if (pct >= 60) return 'bg-emerald-500 text-white';
  if (pct >= 30) return 'bg-amber-500 text-white';
  if (pct > 0) return 'bg-orange-500 text-white';
  return 'bg-stone-300 text-stone-700';
};

export default function ProjectWorkStageTab() {
  const [rows, setRows] = useState(PROJECTS_SEED);
  const [editIdx, setEditIdx] = useState(null);
  const [draft, setDraft] = useState({});

  const startEdit = (i) => { setEditIdx(i); setDraft({ ...rows[i] }); };
  const saveEdit = () => {
    setRows(prev => prev.map((r, i) => i === editIdx ? { ...draft, completion: Number(draft.completion) || 0 } : r));
    setEditIdx(null);
    toast.success('Row updated');
  };
  const addRow = () => {
    setRows(prev => [...prev, {
      srNo: prev.length + 1, project: 'New Project', stage: STAGES[0], completion: 0, contact: 'Sahil', remark: '',
    }]);
    toast.success('Row added');
  };
  const delRow = (i) => {
    setRows(prev => prev.filter((_, idx) => idx !== i).map((r, idx) => ({ ...r, srNo: idx + 1 })));
    toast.success('Row deleted');
  };

  return (
    <div className="space-y-4" data-testid="hr-project-work-stage-tab">
      <div className="flex items-center justify-between bg-emerald-900 text-white px-4 py-3 rounded-lg">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-5 h-5" />
          <div>
            <h2 className="text-lg font-bold">Project Work Stage Report</h2>
            <p className="text-xs text-emerald-200">Stage tracker across all DBK Group projects · {rows.length} projects</p>
          </div>
        </div>
        <Button size="sm" onClick={addRow} className="bg-amber-500 hover:bg-amber-600 text-stone-900 font-semibold" data-testid="add-project-row">
          <Plus className="w-4 h-4 mr-1.5" /> Add Project
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-blue-900 text-white">
                  <th className="px-3 py-2 text-left w-12 border border-blue-800">SR</th>
                  <th className="px-3 py-2 text-left border border-blue-800">PROJECT</th>
                  <th className="px-3 py-2 text-left w-40 border border-blue-800">CURRENT STAGE</th>
                  <th className="px-3 py-2 text-left w-40 border border-blue-800">COMPLETION %</th>
                  <th className="px-3 py-2 text-left w-28 border border-blue-800">CONTACT</th>
                  <th className="px-3 py-2 text-left border border-blue-800">REMARK</th>
                  <th className="px-3 py-2 text-left w-28 border border-blue-800">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-stone-50">
                    <td className="border border-stone-200 px-3 py-2">{r.srNo}</td>
                    <td className="border border-stone-200 px-3 py-2">
                      {editIdx === i
                        ? <input className="w-full border px-1 py-0.5 text-xs" value={draft.project} onChange={e => setDraft(d => ({ ...d, project: e.target.value }))} />
                        : <span className="font-medium">{r.project}</span>}
                    </td>
                    <td className="border border-stone-200 px-3 py-2">
                      {editIdx === i ? (
                        <select className="w-full border px-1 py-0.5 text-xs" value={draft.stage} onChange={e => setDraft(d => ({ ...d, stage: e.target.value }))}>
                          {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : r.stage}
                    </td>
                    <td className="border border-stone-200 px-3 py-2">
                      {editIdx === i
                        ? <input type="number" min={0} max={100} className="w-full border px-1 py-0.5 text-xs" value={draft.completion} onChange={e => setDraft(d => ({ ...d, completion: e.target.value }))} />
                        : (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-stone-200 rounded-full h-3 overflow-hidden">
                              <div className={`h-full ${stageColor(r.completion).split(' ')[0]}`} style={{ width: `${r.completion}%` }} />
                            </div>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${stageColor(r.completion)}`}>{r.completion}%</span>
                          </div>
                        )}
                    </td>
                    <td className="border border-stone-200 px-3 py-2">
                      {editIdx === i
                        ? <input className="w-full border px-1 py-0.5 text-xs" value={draft.contact} onChange={e => setDraft(d => ({ ...d, contact: e.target.value }))} />
                        : r.contact}
                    </td>
                    <td className="border border-stone-200 px-3 py-2">
                      {editIdx === i
                        ? <input className="w-full border px-1 py-0.5 text-xs" value={draft.remark} onChange={e => setDraft(d => ({ ...d, remark: e.target.value }))} />
                        : r.remark}
                    </td>
                    <td className="border border-stone-200 px-3 py-2">
                      <div className="flex gap-1">
                        {editIdx === i ? (
                          <Button size="sm" className="h-6 px-2 bg-green-600 hover:bg-green-700 text-white" onClick={saveEdit}>
                            <Save className="w-3 h-3" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => startEdit(i)}>Edit</Button>
                        )}
                        <Button size="sm" variant="outline" className="h-6 px-2 text-red-600 hover:bg-red-50" onClick={() => delRow(i)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-stone-100 font-bold">
                  <td colSpan={3} className="border border-stone-300 px-3 py-2 text-right">AVERAGE COMPLETION</td>
                  <td className="border border-stone-300 px-3 py-2">
                    {rows.length > 0 ? Math.round(rows.reduce((s, r) => s + (Number(r.completion) || 0), 0) / rows.length) : 0}%
                  </td>
                  <td colSpan={3} className="border border-stone-300 px-3 py-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
